import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import SplashScreen from './components/SplashScreen';
import ScrollToTop from './components/ScrollToTop';
import NetworkStatus from './components/NetworkStatus';
import ErrorBoundary from './components/ErrorBoundary';
import UpdateNotification from './components/UpdateNotification';
import { registerServiceWorker } from './services/pushNotifications';
import { usePresence } from './hooks/usePresence';

// Pages critiques (chargement immédiat)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';

// Pages lazy-loaded (code splitting)
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DevResetLinkPage = lazy(() => import('./pages/DevResetLinkPage'));
const ClubOwnerRegisterPage = lazy(() => import('./pages/ClubOwnerRegisterPage'));
const ClubDashboardPage = lazy(() => import('./pages/ClubDashboardPage'));
const ClubPricingPage = lazy(() => import('./pages/ClubPricingPage'));
const ClubPhotosPage = lazy(() => import('./pages/ClubPhotosPage'));
const ClubReviewsPage = lazy(() => import('./pages/ClubReviewsPage'));
const MatchesPage = lazy(() => import('./pages/MatchesPage'));
const MatchDetailPage = lazy(() => import('./pages/MatchDetailPage'));
const PostMatchCelebrationPage = lazy(() => import('./pages/PostMatchCelebrationPage'));
const LiveScorePage = lazy(() => import('./pages/LiveScorePage'));
const CreateMatchPage = lazy(() => import('./pages/CreateMatchPage'));
const ClubsPage = lazy(() => import('./pages/ClubsPage'));
const ClubDetailPage = lazy(() => import('./pages/ClubDetailPage'));
const RankingPage = lazy(() => import('./pages/RankingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const BookingSuccessPage = lazy(() => import('./pages/BookingSuccessPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const TournamentsPage = lazy(() => import('./pages/TournamentsPage'));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage'));
const CreateTournamentPage = lazy(() => import('./pages/CreateTournamentPage'));
const TournamentOrganizerDashboard = lazy(() => import('./pages/TournamentOrganizerDashboard'));
const LeaguesPage = lazy(() => import('./pages/LeaguesPage'));
const LeagueDetailPage = lazy(() => import('./pages/LeagueDetailPage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const CoachPage = lazy(() => import('./pages/CoachPage'));
const ChallengesPage = lazy(() => import('./pages/ChallengesPage'));
const PartnerSearchPage = lazy(() => import('./pages/PartnerSearchPage'));
const BadgesPage = lazy(() => import('./pages/BadgesPage'));
const DailyRewardsPage = lazy(() => import('./pages/DailyRewardsPage'));
const MissionsPage = lazy(() => import('./pages/MissionsPage'));
const SeasonsPage = lazy(() => import('./pages/SeasonsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminInvoicesPage = lazy(() => import('./pages/AdminInvoicesPage'));
const ClubRevenueDashboard = lazy(() => import('./pages/ClubRevenueDashboard'));
const ClubSettingsPage = lazy(() => import('./pages/ClubSettingsPage'));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'));
const GroupEditPage = lazy(() => import('./pages/GroupEditPage'));
const InviteFriendsPage = lazy(() => import('./pages/InviteFriendsPage'));
const PlayerStatsPage = lazy(() => import('./pages/PlayerStatsPage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const GamificationPage = lazy(() => import('./pages/GamificationPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const ClubStatsPage = lazy(() => import('./pages/ClubStatsPage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const RecurringBookingsPage = lazy(() => import('./pages/RecurringBookingsPage'));
const AICoachingPage = lazy(() => import('./pages/AICoachingPage'));
const SuperAdminGeolocation = lazy(() => import('./pages/SuperAdminGeolocation'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const FavoriteNotificationsPage = lazy(() => import('./pages/FavoriteNotificationsPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const RevenueDashboardPage = lazy(() => import('./pages/RevenueDashboardPage'));
const CouponsAdminPage = lazy(() => import('./pages/CouponsAdminPage'));
const ClubSubscriptionPage = lazy(() => import('./pages/ClubSubscriptionPage'));
const ClubAnalyticsDashboard = lazy(() => import('./pages/ClubAnalyticsDashboard'));
const PlaytomicImportPage = lazy(() => import('./pages/PlaytomicImportPage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const RankProgressPage = lazy(() => import('./pages/RankProgressPage'));

// Club Admin Pages (lazy-loaded)
const ClubAdminLayout = lazy(() => import('./pages/ClubAdminLayout'));
const ClubAdminDashboard = lazy(() => import('./pages/ClubAdminDashboard'));
const ClubAdminCourts = lazy(() => import('./pages/ClubAdminCourts'));
const ClubAdminPricing = lazy(() => import('./pages/ClubAdminPricing'));
const ClubAdminMembers = lazy(() => import('./pages/ClubAdminMembers'));
const ClubAdminStaff = lazy(() => import('./pages/ClubAdminStaff'));
const ClubAdminBookings = lazy(() => import('./pages/ClubAdminBookings'));
const ClubAdminSettings = lazy(() => import('./pages/ClubAdminSettings'));
const ClubAdminAvailability = lazy(() => import('./pages/ClubAdminAvailability'));
const ClubAdminGroupClasses = lazy(() => import('./pages/ClubAdminGroupClasses'));
const GroupClassesPage = lazy(() => import('./pages/GroupClassesPage'));

import './App.css';

// Loading fallback for lazy-loaded components
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0a0f1a]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-400 text-sm">Chargement...</span>
      </div>
    </div>
  );
}

// Presence wrapper to send heartbeats when user is logged in
function PresenceWrapper({ children }) {
  const { user, loading } = useAuth();
  
  // Only use presence hook when not loading and user exists
  // This prevents blocking the app render while auth is checking
  useEffect(() => {
    // Presence is only relevant when user is logged in
  }, [user, loading]);
  
  // Always render children immediately - don't block on auth
  usePresence();
  return children;
}

// Router component that detects session_id synchronously
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously (not in useEffect) to prevent race conditions
  // This detects session_id BEFORE ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dev-reset-link" element={<DevResetLinkPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/club/register" element={<ClubOwnerRegisterPage />} />
      <Route
        path="/club/dashboard"
        element={
          <ProtectedRoute>
            <ClubDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/pricing"
        element={
          <ProtectedRoute>
            <ClubPricingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/photos"
        element={
          <ProtectedRoute>
            <ClubPhotosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/reviews"
        element={
          <ProtectedRoute>
            <ClubReviewsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Club Admin Routes */}
      <Route
        path="/club-admin/:clubId"
        element={
          <ProtectedRoute>
            <ClubAdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ClubAdminDashboard />} />
        <Route path="dashboard" element={<ClubAdminDashboard />} />
        <Route path="courts" element={<ClubAdminCourts />} />
        <Route path="pricing" element={<ClubAdminPricing />} />
        <Route path="availability" element={<ClubAdminAvailability />} />
        <Route path="members" element={<ClubAdminMembers />} />
        <Route path="staff" element={<ClubAdminStaff />} />
        <Route path="bookings" element={<ClubAdminBookings />} />
        <Route path="group-classes" element={<ClubAdminGroupClasses />} />
        <Route path="settings" element={<ClubAdminSettings />} />
        <Route path="analytics" element={<ClubAnalyticsDashboard />} />
        <Route path="coupons" element={<CouponsAdminPage />} />
        <Route path="playtomic-import" element={<PlaytomicImportPage />} />
      </Route>
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches"
        element={
          <ProtectedRoute>
            <MatchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches/:id"
        element={
          <ProtectedRoute>
            <MatchDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches/:matchId/live"
        element={
          <ProtectedRoute>
            <LiveScorePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches/:matchId/celebration"
        element={
          <ProtectedRoute>
            <PostMatchCelebrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-match"
        element={
          <ProtectedRoute>
            <CreateMatchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches/create"
        element={
          <ProtectedRoute>
            <CreateMatchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs"
        element={
          <ProtectedRoute>
            <ClubsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/:id"
        element={
          <ProtectedRoute>
            <ClubDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waitlist"
        element={
          <ProtectedRoute>
            <WaitlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-bookings"
        element={
          <ProtectedRoute>
            <RecurringBookingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-coaching"
        element={
          <ProtectedRoute>
            <AICoachingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ranking"
        element={
          <ProtectedRoute>
            <RankingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites/notifications"
        element={
          <ProtectedRoute>
            <FavoriteNotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral"
        element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute>
            <RevenueDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/coupons"
        element={
          <ProtectedRoute>
            <CouponsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/subscribe"
        element={
          <ProtectedRoute>
            <ClubSubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/analytics"
        element={
          <ProtectedRoute>
            <ClubAnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playtomic-import"
        element={
          <ProtectedRoute>
            <PlaytomicImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking-success"
        element={
          <ProtectedRoute>
            <BookingSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments"
        element={
          <ProtectedRoute>
            <TournamentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/create"
        element={
          <ProtectedRoute>
            <CreateTournamentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id"
        element={
          <ProtectedRoute>
            <TournamentDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id/dashboard"
        element={
          <ProtectedRoute>
            <TournamentOrganizerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leagues"
        element={
          <ProtectedRoute>
            <LeaguesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leagues/:id"
        element={
          <ProtectedRoute>
            <LeagueDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute>
            <AIAssistantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach"
        element={
          <ProtectedRoute>
            <CoachPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/challenges"
        element={
          <ProtectedRoute>
            <ChallengesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners"
        element={
          <ProtectedRoute>
            <PartnerSearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges"
        element={
          <ProtectedRoute>
            <BadgesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-rewards"
        element={
          <ProtectedRoute>
            <DailyRewardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions"
        element={
          <ProtectedRoute>
            <MissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seasons"
        element={
          <ProtectedRoute>
            <SeasonsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gamification"
        element={
          <ProtectedRoute>
            <GamificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <SuperAdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/legacy"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/geolocation"
        element={
          <ProtectedRoute>
            <SuperAdminGeolocation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute>
            <AdminInvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/:clubId/revenue"
        element={
          <ProtectedRoute>
            <ClubRevenueDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club-revenue"
        element={
          <ProtectedRoute>
            <ClubRevenueDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club/:clubId/settings"
        element={
          <ProtectedRoute>
            <ClubSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club-settings"
        element={
          <ProtectedRoute>
            <ClubSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups"
        element={
          <ProtectedRoute>
            <GroupsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId"
        element={
          <ProtectedRoute>
            <GroupDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId/edit"
        element={
          <ProtectedRoute>
            <GroupEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite"
        element={
          <ProtectedRoute>
            <InviteFriendsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <PlayerStatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats/:userId"
        element={
          <ProtectedRoute>
            <PlayerStatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <FeedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy"
        element={
          <ProtectedRoute>
            <PrivacyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/terms"
        element={
          <ProtectedRoute>
            <TermsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/club-stats"
        element={
          <ProtectedRoute>
            <ClubStatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/group-classes"
        element={
          <ProtectedRoute>
            <GroupClassesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <FriendsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <SearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rank-progress"
        element={
          <ProtectedRoute>
            <RankProgressPage />
          </ProtectedRoute>
        }
      />
      {/* Redirections pour compatibilité */}
      <Route path="/activities" element={<Navigate to="/feed" replace />} />
      <Route path="/courses" element={<Navigate to="/group-classes" replace />} />
      <Route path="/chat" element={<Navigate to="/messages" replace />} />
      <Route path="/rankings" element={<Navigate to="/ranking" replace />} />
      <Route path="/statistics" element={<Navigate to="/stats" replace />} />
      <Route path="/notifications" element={<Navigate to="/favorites/notifications" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Splash screen enabled - fixed for React 18 strict mode
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on initial app load (not on page refresh after login)
    return !sessionStorage.getItem('splashShown');
  });
  
  // Register service worker on app load
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      registerServiceWorker().then(registration => {
        if (registration) {
        }
      });
    }
  }, []);

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <PresenceWrapper>
                <BrowserRouter>
                  <ScrollToTop />
                  <NetworkStatus />
                  <UpdateNotification />
                  {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
                  <div className="App min-h-screen theme-bg transition-colors duration-300 w-full overflow-x-hidden">
                    <AppRouter />
                    <Toaster position="top-right" theme="dark" />
                  </div>
                </BrowserRouter>
              </PresenceWrapper>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
